import React, { useState } from "react";
import { withStyles } from '@material-ui/core';
import { Icon } from 'semantic-ui-react';
import { FormattedMessage, useIntl } from 'react-intl';
import * as Constants from '../../Pages/Constants';
import DataServiceSQL from "../../DataService/DataServiceSQL";

/**
 * AddStructurePatient — super-admin modal to create a patient inside a structure.
 * Mirrors the demo-fixtures page: optionally seeds X days of sensor data (by type)
 * and alerts, and optionally creates an associated Keycloak login so the patient
 * can sign in to see their own data.
 *
 * Props: structure (object), cancelPopup(), closeAfterAdd(), accountInformation
 */

// Data-type checkboxes — mirror create_demo_patients.js --with-* (all on by default).
const DATA_TYPES = [
  { key: 'temperature',   labelId: 'structure.patient.type.temperature',   label: 'Température' },
  { key: 'spo2',          labelId: 'structure.patient.type.spo2',          label: 'SpO2 / Pouls' },
  { key: 'bloodpressure', labelId: 'structure.patient.type.bloodpressure', label: 'Tension artérielle' },
  { key: 'weight',        labelId: 'structure.patient.type.weight',        label: 'Poids' },
  { key: 'pain',          labelId: 'structure.patient.type.pain',          label: 'Douleur' },
  { key: 'forms',         labelId: 'structure.patient.type.forms',         label: 'Questionnaires' },
  { key: 'vni',           labelId: 'structure.patient.type.vni',           label: 'Module VNI' },
  { key: 'oxygen',        labelId: 'structure.patient.type.oxygen',        label: 'Module Oxygénothérapie' },
];

const ALL_TYPES_ON = DATA_TYPES.reduce((acc, t) => { acc[t.key] = true; return acc; }, {});

const AddStructurePatient = (props) => {
  const intl = useIntl();
  const { classes, structure } = props;

  const [form, setForm] = useState({ firstname: "", lastname: "", email: "", phone: "" });
  const [bulk, setBulk] = useState("");
  const [createLogin, setCreateLogin] = useState(false);
  const [sendInvitation, setSendInvitation] = useState(true);
  const [dataEnabled, setDataEnabled] = useState(false);
  const [days, setDays] = useState(7);
  const [types, setTypes] = useState({ ...ALL_TYPES_ON });
  const [alertsEnabled, setAlertsEnabled] = useState(false);
  const [alertCount, setAlertCount] = useState("1");
  const [alertTypes, setAlertTypes] = useState("");
  const [alertPast, setAlertPast] = useState("");

  const [submitted, setSubmitted] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleInput = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const toggleType = (key) => {
    setTypes(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const isError = submitFailed || message.toLowerCase().includes('err');

  // The email becomes the patient's login username; a malformed one (e.g. with a
  // "/") breaks username-keyed routes. Require a basic local@domain.tld.
  const EMAIL_RE = /^[^\s/@]+@[^\s/@]+\.[^\s/@]+$/;

  // One bulk line: "lastname, firstname, email, telephone" (trailing fields optional).
  const parseBulkLine = (line) => {
    const parts = line.split(',').map(p => p.trim());
    return { lastname: parts[0] || '', firstname: parts[1] || '', email: parts[2] || '', phone: parts[3] || '' };
  };

  // Shared options (login, demo data, alerts) apply to every created patient.
  const buildPayload = (p) => ({
    firstname: p.firstname,
    lastname: p.lastname,
    email: p.email || undefined,
    phone: p.phone || undefined,
    create_login: createLogin,
    send_invitation: sendInvitation,
    language: intl.locale || 'fr-FR',
    data: { enabled: dataEnabled, days: Number(days) || 7, types },
    alerts: { enabled: alertsEnabled, count: alertCount, types: alertTypes, past: alertPast },
  });

  const savePatient = async () => {
    const bulkText = bulk.trim();
    const isBulk = bulkText.length > 0;

    // When the bulk textarea is filled it drives creation; otherwise the single form does.
    const patients = isBulk
      ? bulkText.split('\n').map(l => l.trim()).filter(Boolean).map(parseBulkLine)
      : [{ firstname: form.firstname.trim(), lastname: form.lastname.trim(), email: form.email.trim(), phone: form.phone.trim() }];

    // Validate every patient up-front so a bad line never produces a partial batch.
    for (let i = 0; i < patients.length; i++) {
      const p = patients[i];
      const where = isBulk
        ? intl.formatMessage({ id: 'structure.patient.error.line', defaultMessage: 'Ligne {n} : ' }, { n: i + 1 })
        : '';
      if (!p.firstname || !p.lastname) {
        setError(where + intl.formatMessage({ id: 'structure.patient.error.name', defaultMessage: 'Prénom et nom sont requis.' }));
        return;
      }
      if (createLogin && !p.email) {
        setError(where + intl.formatMessage({ id: 'structure.patient.error.loginEmail', defaultMessage: "L'email est requis pour créer un compte patient." }));
        return;
      }
      if (p.email && !EMAIL_RE.test(p.email)) {
        setError(where + intl.formatMessage({ id: 'structure.patient.error.emailFormat', defaultMessage: "Adresse email invalide." }));
        return;
      }
    }

    setError("");
    setSubmitFailed(false);
    setIsLoading(true);

    const failures = [];
    let okCount = 0;
    for (let i = 0; i < patients.length; i++) {
      setMessage(patients.length > 1
        ? intl.formatMessage({ id: 'structure.patient.creatingN', defaultMessage: 'Création du patient {n}/{total}…' }, { n: i + 1, total: patients.length })
        : intl.formatMessage({ id: 'structure.patient.creating', defaultMessage: 'Création du patient en cours… (les données peuvent prendre une minute)' }));
      try {
        await DataServiceSQL.createStructurePatient(structure.id_structure, buildPayload(patients[i]));
        okCount++;
      } catch (e) {
        failures.push(`${patients[i].lastname} ${patients[i].firstname}`.trim());
      }
    }

    if (failures.length === 0) {
      setMessage(patients.length > 1
        ? intl.formatMessage({ id: 'structure.patient.createdN', defaultMessage: '{n} patient(s) créé(s) avec succès.' }, { n: okCount })
        : intl.formatMessage({ id: 'message.success', defaultMessage: 'Success!' }));
    } else if (okCount === 0 && patients.length === 1) {
      setSubmitFailed(true);
      setMessage(intl.formatMessage({ id: 'message.error', defaultMessage: 'An error occurred' }));
    } else {
      setSubmitFailed(true);
      setMessage(intl.formatMessage(
        { id: 'structure.patient.createdPartial', defaultMessage: '{ok} créé(s), {fail} en échec : {names}' },
        { ok: okCount, fail: failures.length, names: failures.join(', ') }
      ));
    }
    setSubmitted(true);
    setIsLoading(false);
  };

  const sectionTitle = (txt) => (
    <p style={{ fontSize: "0.75em", fontWeight: 700, textTransform: "uppercase", color: "#888", letterSpacing: "0.08em", margin: "0 0 10px" }}>{txt}</p>
  );

  return (
    <div className={classes.popup_background} onClick={props.cancelPopup}>
      <div style={{ minHeight: "100%", display: "flex", flexDirection: "column", justifyContent: "center" }} onClick={(e) => e.stopPropagation()}>
        <div className="management-menu-popup" style={{ height: "auto", maxHeight: "92vh", overflowY: "auto", position: "relative", maxWidth: "600px" }}>

          <button
            onClick={props.cancelPopup}
            style={{ position: 'absolute', top: 6, right: 20, zIndex: 10, background: '#fff', border: '1px solid #ccc', borderRadius: '50%', fontSize: 18, fontWeight: 'bold', lineHeight: 1, cursor: 'pointer', color: '#DB2828', width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.15)' }}
            title={intl.formatMessage({ id: 'button.close', defaultMessage: 'Fermer' })}
          >×</button>

          <div style={{ display: "flex", alignItems: "center", marginBottom: "18px", borderBottom: "2px solid #f0f0f0", paddingBottom: "12px" }}>
            <Icon name="user plus" size="large" color="green" />
            <h5 style={{ margin: "0 0 0 10px", fontWeight: 600, color: "#2c3e50" }}>
              <FormattedMessage id="structure.patient.add" defaultMessage="Ajouter un patient" />
              {structure && structure.structure_name && (
                <span style={{ color: "#888", fontWeight: 400 }}> — {structure.structure_name}</span>
              )}
            </h5>
          </div>

          {error && <div className="alert alert-danger" style={{ borderRadius: "8px", fontSize: "0.9em" }}>{error}</div>}

          {submitted ? (
            <div style={{ textAlign: "center", padding: "16px 0" }}>
              <Icon name={isError ? "times circle" : "check circle"} size="huge" color={isError ? "red" : "green"} />
              <p style={{ marginTop: "12px", fontSize: "1em", color: "#555" }}>{message}</p>
              <button className="btn btn-success" onClick={props.closeAfterAdd} style={{ marginTop: "8px", borderRadius: "6px" }}>
                <FormattedMessage id="button.close" defaultMessage="Close" />
              </button>
            </div>
          ) : (
            <>
              {/* Identity */}
              <div style={{ marginBottom: "18px" }}>
                {sectionTitle(intl.formatMessage({ id: 'structure.patient.section.identity', defaultMessage: 'Identité' }))}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <label style={{ fontWeight: 600, fontSize: "0.88em", color: "#444", marginBottom: "4px", display: "block" }}>
                      <FormattedMessage id="patient.firstname" defaultMessage="Prénom" /> <span style={{ color: "#e74c3c" }}>*</span>
                    </label>
                    <input type="text" className="form-control" name="firstname" value={form.firstname} onChange={handleInput} style={{ borderRadius: "7px" }} />
                  </div>
                  <div>
                    <label style={{ fontWeight: 600, fontSize: "0.88em", color: "#444", marginBottom: "4px", display: "block" }}>
                      <FormattedMessage id="patient.lastname" defaultMessage="Nom" /> <span style={{ color: "#e74c3c" }}>*</span>
                    </label>
                    <input type="text" className="form-control" name="lastname" value={form.lastname} onChange={handleInput} style={{ borderRadius: "7px" }} />
                  </div>
                  <div>
                    <label style={{ fontWeight: 600, fontSize: "0.88em", color: "#444", marginBottom: "4px", display: "block" }}>
                      <Icon name="mail outline" style={{ marginRight: "4px" }} />
                      <FormattedMessage id="structure.email" defaultMessage="Email" />
                    </label>
                    <input type="email" className="form-control" name="email" value={form.email} onChange={handleInput} style={{ borderRadius: "7px" }} />
                  </div>
                  <div>
                    <label style={{ fontWeight: 600, fontSize: "0.88em", color: "#444", marginBottom: "4px", display: "block" }}>
                      <Icon name="phone" style={{ marginRight: "4px" }} />
                      <FormattedMessage id="structure.phone" defaultMessage="Téléphone" />
                    </label>
                    <input type="text" className="form-control" name="phone" value={form.phone} onChange={handleInput} style={{ borderRadius: "7px" }} />
                  </div>
                </div>
              </div>

              {/* Bulk creation */}
              <div style={{ marginBottom: "18px" }}>
                {sectionTitle(intl.formatMessage({ id: 'structure.patient.section.bulk', defaultMessage: 'Création en masse (optionnel)' }))}
                <p style={{ fontSize: "0.8em", color: "#888", margin: "0 0 6px" }}>
                  <FormattedMessage id="structure.patient.bulk.hint" defaultMessage="Un patient par ligne au format : nom, prénom, email, téléphone. Si rempli, le formulaire d'identité ci-dessus est ignoré et un patient est créé par ligne." />
                </p>
                <textarea
                  className="form-control"
                  rows={4}
                  name="bulk"
                  value={bulk}
                  onChange={e => setBulk(e.target.value)}
                  placeholder={"Dupont, Marie, marie.dupont@mail.fr, 0612345678\nMartin, Paul, paul.martin@mail.fr, 0698765432"}
                  style={{ borderRadius: "7px", fontFamily: "monospace", fontSize: "0.82em" }}
                />
              </div>

              {/* Patient login */}
              <div style={{ marginBottom: "18px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", userSelect: "none" }}>
                  <input type="checkbox" checked={createLogin} onChange={e => setCreateLogin(e.target.checked)} style={{ width: 16, height: 16, cursor: "pointer" }} />
                  <span style={{ fontWeight: 600, fontSize: "0.88em", color: "#444" }}>
                    <Icon name="key" style={{ marginRight: "2px" }} />
                    <FormattedMessage id="structure.patient.createLogin" defaultMessage="Créer un compte patient (connexion Keycloak pour voir ses données)" />
                  </span>
                </label>
                {createLogin && (
                  <div style={{ marginTop: "8px", paddingLeft: "24px" }}>
                    <label style={{ display: "flex", alignItems: "flex-start", gap: "8px", cursor: "pointer", userSelect: "none", margin: 0 }}>
                      <input type="checkbox" checked={sendInvitation} onChange={e => setSendInvitation(e.target.checked)} style={{ width: 15, height: 15, marginTop: 2, cursor: "pointer", flexShrink: 0 }} />
                      <span style={{ fontSize: "0.82em", color: "#666", fontWeight: "normal" }}>
                        <Icon name="mail outline" style={{ marginRight: "2px" }} />
                        <FormattedMessage id="structure.patient.sendInvitation" defaultMessage="Un email d'invitation avec les identifiants sera envoyé à l'adresse renseignée." />
                      </span>
                    </label>
                  </div>
                )}
              </div>

              {/* Demo data */}
              <div style={{ marginBottom: "18px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", userSelect: "none" }}>
                  <input type="checkbox" checked={dataEnabled} onChange={e => setDataEnabled(e.target.checked)} style={{ width: 16, height: 16, cursor: "pointer" }} />
                  <span style={{ fontWeight: 600, fontSize: "0.88em", color: "#444" }}>
                    <Icon name="chart line" style={{ marginRight: "2px" }} />
                    <FormattedMessage id="structure.patient.genData" defaultMessage="Générer des données de démo" />
                  </span>
                </label>
                {dataEnabled && (
                  <div style={{ marginTop: "10px", paddingLeft: "24px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
                      <span style={{ fontWeight: 600, fontSize: "0.85em", color: "#555" }}>
                        <FormattedMessage id="structure.patient.days" defaultMessage="Nombre de jours" />
                      </span>
                      <input type="range" min={1} max={90} step={1} value={days} onChange={e => setDays(e.target.value)} style={{ flex: 1, minWidth: 0 }} />
                      <input type="number" min={1} max={90} value={days} onChange={e => setDays(e.target.value)} className="form-control form-control-sm" style={{ width: 70 }} />
                    </div>
                    <fieldset style={{ border: '1px solid #d0d7e2', borderRadius: 6, padding: '6px 10px 10px' }}>
                      <legend style={{ fontSize: '0.78em', fontWeight: 'bold', color: '#1a73e8', padding: '0 6px', width: 'auto', marginBottom: 0 }}>
                        <FormattedMessage id="structure.patient.types" defaultMessage="Types de données à créer" />
                      </legend>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: "4px 12px" }}>
                        {DATA_TYPES.map(t => (
                          <label key={t.key} style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", fontWeight: "normal", margin: 0 }}>
                            <input type="checkbox" checked={!!types[t.key]} onChange={() => toggleType(t.key)} />
                            <span style={{ fontSize: "0.85em" }}>
                              <FormattedMessage id={t.labelId} defaultMessage={t.label} />
                            </span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  </div>
                )}
              </div>

              {/* Alerts */}
              <div style={{ marginBottom: "18px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", userSelect: "none" }}>
                  <input type="checkbox" checked={alertsEnabled} onChange={e => setAlertsEnabled(e.target.checked)} style={{ width: 16, height: 16, cursor: "pointer" }} />
                  <span style={{ fontWeight: 600, fontSize: "0.88em", color: "#444" }}>
                    <Icon name="bell" style={{ marginRight: "2px" }} />
                    <FormattedMessage id="structure.patient.genAlerts" defaultMessage="Ajouter des alertes" />
                  </span>
                </label>
                {alertsEnabled && (
                  <div style={{ marginTop: "10px", paddingLeft: "24px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px" }}>
                    <div>
                      <label style={{ fontWeight: 600, fontSize: "0.8em", color: "#555", marginBottom: "3px", display: "block" }}>
                        <FormattedMessage id="structure.patient.alertCount" defaultMessage="Nombre (ex: 2 ou 2-5)" />
                      </label>
                      <input type="text" className="form-control form-control-sm" value={alertCount} onChange={e => setAlertCount(e.target.value)} style={{ borderRadius: "6px" }} />
                    </div>
                    <div>
                      <label style={{ fontWeight: 600, fontSize: "0.8em", color: "#555", marginBottom: "3px", display: "block" }}>
                        <FormattedMessage id="structure.patient.alertPast" defaultMessage="Passées (ex: 1-3)" />
                      </label>
                      <input type="text" className="form-control form-control-sm" value={alertPast} onChange={e => setAlertPast(e.target.value)} style={{ borderRadius: "6px" }} />
                    </div>
                    <div>
                      <label style={{ fontWeight: 600, fontSize: "0.8em", color: "#555", marginBottom: "3px", display: "block" }}>
                        <FormattedMessage id="structure.patient.alertTypes" defaultMessage="Types (optionnel)" />
                      </label>
                      <input type="text" className="form-control form-control-sm" placeholder="fever,spo2,pain…" value={alertTypes} onChange={e => setAlertTypes(e.target.value)} style={{ borderRadius: "6px" }} />
                    </div>
                  </div>
                )}
              </div>

              {message && <p style={{ textAlign: "center", color: "#888", fontSize: "0.9em" }}>{message}</p>}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", borderTop: "1px solid #f0f0f0", paddingTop: "16px" }}>
                <button className="btn btn-outline-secondary" onClick={props.cancelPopup} style={{ borderRadius: "7px", minWidth: "90px" }}>
                  <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
                </button>
                <button className="btn btn-success" onClick={savePatient} disabled={isLoading} style={{ borderRadius: "7px", minWidth: "120px", fontWeight: 600 }}>
                  {isLoading
                    ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    : <><Icon name="save outline" /><FormattedMessage id="button.save" defaultMessage="Save" /></>
                  }
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default withStyles(Constants.styles)(AddStructurePatient);
